import type { RuntimeEnvironment } from '../../types';

const MANIFEST_FILE = 'mf-manifest.json';

// Important: New MFE must be added to this object to be able to use the public path from the environment variables.
const PUBLIC_PATH_BY_REMOTE = {
  'customer-expense': 'VITE_CUSTOMER_EXPENSE_PUBLIC_PATH',
  'customer-pay-supplement': 'VITE_CUSTOMER_PAY_SUPPLEMENT_PUBLIC_PATH',
  'customer-payroll': 'VITE_CUSTOMER_PAYROLL_PUBLIC_PATH',
} as const;

type KnownRemote = keyof typeof PUBLIC_PATH_BY_REMOTE;

/** Env key for a remote name, or a `remote/expose` id. */
const publicPathEnvKeyFor = (remoteIdOrName: string): string | undefined => {
  const remoteName = remoteIdOrName.split('/')[0];
  if (!remoteName) {
    return undefined;
  }
  return PUBLIC_PATH_BY_REMOTE[remoteName as KnownRemote];
};

/** Public-path origin from env for a remote name or `remote/expose` id. */
export const getPublicPathEnvKey = (
  env: RuntimeEnvironment,
  remoteIdOrName: string,
): string | undefined => {
  const key = publicPathEnvKeyFor(remoteIdOrName);
  if (!key) {
    return undefined;
  }

  const value = env[key];
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.replace(/\/$/, '');
};

export const toManifestEntry = (
  publicPath: string | undefined,
): string | undefined => {
  if (typeof publicPath !== 'string') {
    return undefined;
  }
  const trimmed = publicPath.trim();
  if (!trimmed) {
    return undefined;
  }
  return `${trimmed.replace(/\/$/, '')}/${MANIFEST_FILE}`;
};

export const toManifestEntryFromEnv = (
  env: RuntimeEnvironment,
  remoteIdOrName: string,
): string | undefined =>
  toManifestEntry(getPublicPathEnvKey(env, remoteIdOrName));

export default toManifestEntryFromEnv;
